import React, { useEffect, useState } from "react";
import {
  Eye,
  FileSpreadsheet,
  Inbox,
  Loader2,
  Lock,
  ScanText,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { authConfig } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { BrandIcon, BrandStripe, BrandWordmark } from "@/components/BrandLogo";
import { ThemeToggle } from "@/theme/ThemeToggle";

/* Punchy value props — each gets one goSTOPS hue on its icon tile. */
const FEATURES = [
  {
    icon: Inbox,
    title: "Inbox, auto-triaged",
    body: "Payment mail sorted into advances, approvals and follow-ups.",
    tile: "bg-[hsl(var(--stripe-purple))] text-white",
  },
  {
    icon: ScanText,
    title: "16 fields, extracted",
    body: "Claude reads PDFs, images and ZIPs. PAN and IFSC validated.",
    tile: "bg-[hsl(var(--stripe-pink))] text-white",
  },
  {
    icon: FileSpreadsheet,
    title: "Mysa-ready CSV",
    body: "Approved rows only, in your exact column order.",
    tile: "bg-[hsl(var(--brand-teal))] text-[hsl(222_47%_11%)]",
  },
];

/* Decorative capsules echoing the logo's hand motif — true brand hues, no
   alpha (alpha over yellow goes muddy). Kept clear of all panel copy. */
const BLOBS = [
  {
    token: "--stripe-sky",
    box: "right-[40%] top-[-8%] h-[220px] w-[62px]",
    rot: "-24deg",
    delay: "0s",
  },
  {
    token: "--stripe-purple",
    box: "right-[28%] top-[-12%] h-[250px] w-[66px]",
    rot: "-8deg",
    delay: "1.2s",
  },
  {
    token: "--stripe-pink",
    box: "right-[17%] top-[-10%] h-[235px] w-[64px]",
    rot: "10deg",
    delay: "0.6s",
  },
  {
    token: "--stripe-orange",
    box: "right-[5%] top-[-2%] h-[190px] w-[58px]",
    rot: "30deg",
    delay: "1.8s",
  },
  {
    token: "--brand-teal",
    box: "left-[-4%] bottom-[4%] hidden h-[190px] w-[58px] lg:block",
    rot: "-58deg",
    delay: "2.4s",
  },
];

export default function LoginPage() {
  const { login, loading } = useAuth();
  const [config, setConfig] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  useEffect(() => {
    authConfig()
      .then(setConfig)
      .catch(() => setConfig(null));
  }, []);

  const domains = (config?.allowed_domains || ["gostops.com"]).map((d) => `@${d}`).join(", ");

  return (
    <div className="flex min-h-screen flex-col bg-background" data-testid="login-page">
      <BrandStripe />

      <div className="grid flex-1 lg:grid-cols-[1.08fr_minmax(420px,480px)]">
        {/* ---------------- brand panel ---------------- */}
        <section
          className="vde-login-panel relative isolate flex flex-col justify-between overflow-hidden px-6 py-7 sm:px-10 sm:py-10 lg:px-14 lg:py-14"
          data-testid="login-brand-panel"
        >
          {/* decorative layer */}
          <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
            {BLOBS.map(({ token, box, rot, delay }) => (
              <span
                key={token + box}
                className={`vde-login-blob ${box}`}
                style={{
                  background: `hsl(var(${token}))`,
                  "--blob-rot": rot,
                  animationDelay: delay,
                }}
              />
            ))}
            {/* soft light pool + logo mark. The mark sits on its own yellow
                disc: in light mode that disc matches the panel exactly (so the
                mark looks native), in dark mode the whole medallion is dimmed
                as one piece instead of showing a muddy square. */}
            <span className="absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full bg-[hsl(var(--panel-tile)/0.32)] blur-3xl" />
            <img
              src="/brand/gostops-icon.jpeg"
              alt=""
              className="absolute -right-10 top-1/2 hidden h-[360px] w-[360px] -translate-y-1/2 rotate-3 object-cover [mask-image:radial-gradient(circle_at_center,#000_52%,transparent_60%)] dark:opacity-75 lg:block xl:h-[440px] xl:w-[440px]"
              draggable={false}
            />
          </div>

          {/* wordmark tile — always on a solid surface */}
          <div className="vde-rise relative flex w-fit items-center gap-3 rounded-2xl border border-[hsl(var(--panel-ink)/0.08)] bg-[hsl(var(--panel-tile))] px-4 py-3 shadow-[0_12px_30px_-18px_hsl(var(--panel-ink)/0.55)]">
            <BrandWordmark className="h-8 sm:h-9" />
            <span className="h-9 w-px bg-[hsl(var(--panel-ink)/0.12)]" aria-hidden="true" />
            <span className="block">
              <span className="block text-[13px] font-bold leading-tight tracking-tight text-[hsl(var(--panel-ink))]">
                Vendor Detail Extractor
              </span>
              <span className="block text-[11px] text-[hsl(var(--panel-ink-soft))]">
                Payments inbox · Claude vision · Mysa CSV
              </span>
            </span>
          </div>

          <div className="relative mt-8 max-w-[600px] sm:mt-10 lg:mt-0">
            <span
              className="vde-rise hidden items-center gap-2 rounded-full border border-[hsl(var(--panel-ink)/0.14)] bg-[hsl(var(--panel-tile)/0.72)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--panel-ink-soft))] backdrop-blur-sm sm:inline-flex"
              style={{ animationDelay: "60ms" }}
            >
              Internal tool · finance ops
            </span>

            <h1
              className="vde-rise mt-4 text-[28px] font-extrabold leading-[1.12] tracking-tight text-[hsl(var(--panel-ink))] sm:mt-5 sm:text-[40px] lg:text-[46px]"
              style={{ animationDelay: "120ms" }}
              data-testid="login-heading"
            >
              Vendor master data,
              <br />
              straight out of the
              <br />
              payments inbox.
            </h1>

            <p
              className="vde-rise mt-3 max-w-[46ch] text-sm leading-relaxed text-[hsl(var(--panel-ink-soft))] sm:mt-4 sm:text-[15px]"
              style={{ animationDelay: "180ms" }}
            >
              Sign in with your goSTOPS Google account to review synced payment mail, fix extracted
              vendor rows and export the approved ones.
            </p>

            <ul className="mt-6 grid gap-2.5 sm:mt-8 sm:grid-cols-3 sm:gap-3" data-testid="login-features">
              {FEATURES.map(({ icon: Icon, title, body, tile }, i) => (
                <li
                  key={title}
                  className="vde-rise flex items-center gap-3 rounded-2xl border border-[hsl(var(--panel-ink)/0.08)] bg-[hsl(var(--panel-tile))] p-3 shadow-[0_14px_30px_-22px_hsl(var(--panel-ink)/0.6)] sm:block sm:p-4"
                  style={{ animationDelay: `${240 + i * 70}ms` }}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tile}`}
                  >
                    <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold tracking-tight text-[hsl(var(--panel-ink))] sm:mt-3">
                      {title}
                    </p>
                    <p className="mt-1 hidden text-[11.5px] leading-relaxed text-[hsl(var(--panel-ink-soft))] sm:block">
                      {body}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ---------------- sign in ---------------- */}
        <section className="flex flex-col border-t border-border bg-background px-5 py-6 sm:px-8 sm:py-8 lg:border-l lg:border-t-0 lg:px-10 lg:py-10">
          <div className="flex justify-end">
            <ThemeToggle />
          </div>

          <div className="flex flex-1 items-center justify-center">
            <div
              className="vde-card vde-rise relative w-full max-w-[380px] overflow-hidden p-6 sm:p-7"
              data-testid="login-card"
            >
              {/* decorative brand tile — non-interactive, never carries text */}
              <span
                className="pointer-events-none absolute -right-7 -top-7 h-20 w-20 rotate-12 rounded-2xl bg-[hsl(var(--stripe-yellow))] opacity-90"
                aria-hidden="true"
              />

              <div className="relative flex items-center gap-3">
                <BrandIcon size="h-10 w-10" />
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-secondary text-primary">
                  <Lock className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
              </div>

              <h2 className="relative mt-4 text-lg font-bold tracking-tight">Sign in to continue</h2>
              <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">
                Access is limited to{" "}
                <span className="font-semibold text-foreground" data-testid="login-allowed-domains">
                  {domains}
                </span>{" "}
                Google accounts.
              </p>

              <Button
                className="mt-5 h-11 w-full gap-2 text-sm active:scale-[0.98]"
                onClick={() => {
                  setRedirecting(true);
                  login();
                }}
                disabled={redirecting || loading}
                data-testid="login-google-button"
              >
                {redirecting ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden="true">
                    <path
                      fill="#FFC107"
                      d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"
                    />
                    <path
                      fill="#FF3D00"
                      d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
                    />
                    <path
                      fill="#4CAF50"
                      d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-8l-6.5 5C9.6 39.6 16.2 44 24 44z"
                    />
                    <path
                      fill="#1976D2"
                      d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.2-4.1 5.6l6.2 5.2C40.9 36.3 44 30.6 44 24c0-1.2-.1-2.3-.4-3.5z"
                    />
                  </svg>
                )}
                Continue with Google
              </Button>

              <div className="mt-5 space-y-2.5 border-t border-border pt-5">
                <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                  What you get
                </p>
                <div className="flex items-start gap-2.5">
                  <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                  <p className="text-[12px] leading-relaxed">
                    <span className="font-semibold">Admin</span> — connect Gmail, change settings and
                    run exports. Every action is written to the activity log.
                  </p>
                </div>
                <div className="flex items-start gap-2.5">
                  <Eye className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                  <p className="text-[12px] leading-relaxed">
                    <span className="font-semibold">Viewer</span> — read-only access to the payments
                    inbox and the extraction queue.
                  </p>
                </div>
              </div>

              {config?.needs_bootstrap ? (
                <p
                  className="mt-4 rounded-lg border border-warning-border bg-warning-surface px-3 py-2 text-[11.5px] leading-relaxed text-warning-text"
                  data-testid="login-bootstrap-note"
                >
                  Nobody has signed in yet — the first goSTOPS account to sign in becomes the admin
                  and can invite the rest of the team.
                </p>
              ) : null}
            </div>
          </div>

          <div className="mt-6 space-y-2">
            <p
              className="text-center text-[11.5px] leading-relaxed text-muted-foreground"
              data-testid="login-help-note"
            >
              Trouble signing in? Ask a workspace admin to add your goSTOPS account.
            </p>
            <p
              className="text-center text-[11px] leading-relaxed text-muted-foreground/80"
              data-testid="login-security-note"
            >
              Payment mail, attachments and OAuth tokens stay inside this workspace. Tokens are
              encrypted at rest and only{" "}
              <span className="vde-code font-semibold text-foreground/80">
                payments.tools@gostops.com
              </span>{" "}
              is synced.
            </p>
          </div>

          <BrandStripe className="mt-6 hidden rounded-full lg:block" />
        </section>
      </div>
    </div>
  );
}
